import { useCallback } from "react";
import ReduxFetchState from "redux-fetch-state";
import { put, takeEvery } from "redux-saga/effects";
import axios from "../../../../Utils/axiosConfig";
import { getCurrentLanguage } from "../../../../Utils/sagaHelpers";
import { useAppDispatch, useAppSelector } from "../../../../Hooks/useStore";
import { alert } from "../../../../Utils/toast";

export type GetRealTimeChatPayload = {
  question: string;
  answer: string;
  answer_id: string;
  userSessionId: string | number;

  usage: {
    input_tokens: number;
    output_tokens: number;
    total_tokens: number;
  };

  signal?: AbortSignal;
};

export type GetRealTimeChatLoadAction = {
  type: string;
  payload: GetRealTimeChatPayload;
};

export type GetRealTimeChatData = {
  data: {
    value: string;
  };
};

const GetRealTimeChat =
    new ReduxFetchState<
        GetRealTimeChatData,
        GetRealTimeChatPayload,
        any
    >("getRealTimeChat");


export function* watchGetRealTimeChat(
    action: GetRealTimeChatLoadAction
) {
  try {
    const lang: "fa" | "en" = yield getCurrentLanguage();

    const {
      signal,
      ...payload
    } = action.payload;


    const response: GetRealTimeChatData =
        yield axios({
          url: `api/v1/ai/third-party/${payload.userSessionId}/get-real-time-chat`,
          method: "POST",
          headers: {
            lang,
          },
          data: payload,
          signal,
        });


    yield put(
        GetRealTimeChat.actions.loadSuccess({
          data: response.data,
        })
    );

  } catch (e: any) {
    const message =
        e?.response?.data?.error?.message?.[0] ||
        e?.response?.data?.message ||
        e?.response?.data?.error ||
        e?.message ||
        "Something went wrong";

    alert({
      message:
          typeof message === "string"
              ? message
              : "Something went wrong",
      type: "error",
    });


    yield put(
        GetRealTimeChat.actions.loadFailure(
            typeof message === "string"
                ? message
                : "Something went wrong"
        )
    );
  }
}

export function* getRealTimeChatSagas() {
  yield takeEvery(GetRealTimeChat.actionTypes.load, watchGetRealTimeChat);
}

export function useGetRealTimeChat() {
  const { loading, data, ...getRealTimeChatState } = useAppSelector(
      (state) => state.getRealTimeChat,
  );
  const dispatch = useAppDispatch();

  const dispatchGetRealTimeChat = useCallback(
      (data:GetRealTimeChatPayload) => {
        dispatch(GetRealTimeChat.actions.load(data));
      },
      [dispatch],
  );

  return {
    ...getRealTimeChatState,
    dispatchGetRealTimeChat,
    getRealTimeChatLoading: loading,
    getRealTimeChatData: data,
  };
}

export const {
  actionTypes: getRealTimeChatActionsTypes,
  actions: getRealTimeChatActions,
  reducer: getRealTimeChatReducer,
} = GetRealTimeChat;